import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Loader2, CalendarDays, CheckCircle2, Plus, Mail } from "lucide-react";

/**
 * Reusable Reiki rebook modal — mirrors the public new-booking flow but
 * reuses the client's saved Stripe Customer + PaymentMethod so no Stripe
 * redirect is needed. Shared between the Admin bookings dashboard (Holly
 * rebooking on a client's behalf) and the client dashboard's "Book
 * another session" quick link.
 *
 * Props:
 *   source   — the existing reiki_bookings doc that carries the card on
 *              file. Only `lead_name / user_email / lead_phone / id /
 *              tier_slug / attendees / intention / physical_limitations`
 *              are read.
 *   endpoint — POST target. Defaults to the admin endpoint; the client
 *              flow passes `/reiki/bookings/{id}/rebook` instead.
 *   onClose  — dismiss the modal (no action taken).
 *   onSaved  — called after a successful POST so the parent can refetch.
 */

function isoLocalDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

export default function RebookModal({ source, endpoint, onClose, onSaved }) {
  const submitUrl = endpoint || `/admin/reiki/bookings/${source.id}/rebook`;

  const [tiers, setTiers] = useState([]);
  const [tierSlug, setTierSlug] = useState(source.tier_slug);
  const [attendees, setAttendees] = useState(
    (source.attendees || []).map((a) => ({ name: a.name || "", email: a.email || "" }))
  );
  const [intention, setIntention] = useState(source.intention || "");
  const [physicalLimits, setPhysicalLimits] = useState(source.physical_limitations || "");
  const [dateOptions, setDateOptions] = useState([]);
  const [selectedDate, setSelectedDate] = useState("");
  const [availableDays, setAvailableDays] = useState(null);
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [slot, setSlot] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/reiki/tiers")
      .then(({ data }) => setTiers(data.tiers || []))
      .catch(() => setTiers([]));
  }, []);

  const tier = useMemo(() => tiers.find((t) => t.slug === tierSlug) || null, [tiers, tierSlug]);

  useEffect(() => {
    if (!tier) return;
    const required = Math.max(0, (tier.min_attendees || 1) - 1);
    setAttendees((prev) => {
      const trimmed = prev.slice(0, Math.max(0, (tier.max_attendees || 1) - 1));
      while (trimmed.length < required) trimmed.push({ name: "", email: "" });
      return trimmed;
    });
  }, [tier]);

  useEffect(() => {
    const out = [];
    const start = new Date();
    const end = new Date(start.getFullYear(), 10, 30);
    if (end < start) end.setFullYear(end.getFullYear() + 1);
    const cursor = new Date(start);
    let guard = 0;
    while (cursor <= end && guard < 200) {
      out.push({
        value: isoLocalDate(cursor),
        label: cursor.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" }),
      });
      cursor.setDate(cursor.getDate() + 1);
      guard += 1;
    }
    setDateOptions(out);
    if (out.length > 0) setSelectedDate(out[0].value);
  }, []);

  useEffect(() => {
    if (!tier || dateOptions.length === 0) return;
    const first = dateOptions[0].value;
    const last = dateOptions[dateOptions.length - 1].value;
    let cancelled = false;
    setAvailableDays(null);
    api.get(`/reiki/available-days?tier=${tier.slug}&start=${first}&end=${last}`)
      .then(({ data }) => { if (!cancelled) setAvailableDays(new Set(data.days || [])); })
      .catch(() => { if (!cancelled) setAvailableDays(new Set()); });
    return () => { cancelled = true; };
  }, [tier, dateOptions]);

  useEffect(() => {
    if (!tier || !selectedDate) return;
    setLoadingSlots(true);
    setSlot("");
    api.get(`/reiki/availability?tier=${tier.slug}&day=${selectedDate}`)
      .then(({ data }) => setSlots(data.slots || []))
      .catch(() => setSlots([]))
      .finally(() => setLoadingSlots(false));
  }, [tier, selectedDate]);

  const totalPrice = useMemo(() => {
    if (!tier) return 0;
    if (tier.slug === "semi-private") return 185;
    if (tier.slug.startsWith("group-")) {
      const n = 1 + attendees.length;
      return n * tier.per_person_usd;
    }
    return tier.price_usd;
  }, [tier, attendees.length]);

  const submit = async () => {
    if (!tier) { toast.error("Pick a session type"); return; }
    if (!slot) { toast.error("Pick a date + time"); return; }
    const needsAttendees = tier.slug === "semi-private" || tier.slug.startsWith("group-");
    if (needsAttendees) {
      const filled = attendees.filter((a) => a.name.trim()).length;
      const requiredExtras = (tier.min_attendees || 1) - 1;
      if (filled < requiredExtras) {
        toast.error(`Please add ${requiredExtras} attendee name${requiredExtras === 1 ? "" : "s"}`);
        return;
      }
    }
    setSubmitting(true);
    try {
      const payload = {
        tier_slug: tier.slug,
        start_iso: slot,
        attendees: attendees.filter((a) => a.name.trim()).map((a) => ({
          name: a.name.trim(),
          email: (a.email || "").trim(),
        })),
        intention: intention.trim(),
        physical_limitations: physicalLimits.trim(),
      };
      await api.post(submitUrl, payload);
      toast.success("Rebooked — confirmation email sent");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rebook failed");
    } finally {
      setSubmitting(false);
    }
  };

  const fmtSlot = (iso) => new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

  return (
    <div className="fixed inset-0 z-50 bg-aviana-ink/60 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-4" onClick={onClose} data-testid="reiki-rebook-modal">
      <div className="bg-white rounded-2xl w-full max-w-3xl my-8 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-aviana-ink/10">
          <div>
            <p className="text-[10px] uppercase tracking-[0.22em] text-aviana-gold">Rebook</p>
            <h2 className="font-display text-2xl text-aviana-ink">{source.lead_name}</h2>
          </div>
          <button onClick={onClose} className="text-aviana-muted hover:text-aviana-ink text-2xl leading-none" data-testid="reiki-rebook-close">×</button>
        </div>

        <div className="p-6 space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-aviana-muted mb-0.5">Name</p>
              <p className="text-aviana-ink">{source.lead_name}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-aviana-muted mb-0.5">Email</p>
              <p className="text-aviana-ink truncate">{source.user_email}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-aviana-muted mb-0.5">Phone</p>
              <p className="text-aviana-ink">{source.lead_phone}</p>
            </div>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-wider text-aviana-muted mb-1.5">Session type</label>
            <select
              value={tierSlug}
              onChange={(e) => setTierSlug(e.target.value)}
              className="w-full border border-aviana-ink/15 rounded-md px-3 py-2.5 text-sm bg-white"
              data-testid="reiki-rebook-tier"
            >
              {tiers.map((t) => (
                <option key={t.slug} value={t.slug}>
                  {t.name} — ${t.slug === "semi-private" ? "185" : t.slug.startsWith("group-") ? `${t.per_person_usd}/person` : t.price_usd} · {t.duration_min} min
                </option>
              ))}
            </select>
            {tier && (
              <p className="mt-1.5 text-xs text-aviana-plum">
                <strong>${totalPrice}</strong> total for this session
                {tier.slug.startsWith("group-") && ` (${1 + attendees.length} attendees × $${tier.per_person_usd})`}
              </p>
            )}
          </div>

          {tier && (tier.slug === "semi-private" || tier.slug.startsWith("group-")) && (
            <div data-testid="reiki-rebook-attendees">
              <label className="block text-xs uppercase tracking-wider text-aviana-muted mb-1.5">
                Additional attendees ({tier.min_attendees - 1}–{tier.max_attendees - 1} needed beyond {source.lead_name})
              </label>
              <div className="space-y-2">
                {attendees.map((a, i) => (
                  <div key={i} className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <input
                      type="text"
                      placeholder={`Attendee ${i + 2} name`}
                      value={a.name}
                      onChange={(e) => setAttendees((prev) => prev.map((x, j) => j === i ? { ...x, name: e.target.value } : x))}
                      className="border border-aviana-ink/15 rounded-md px-3 py-2 text-sm"
                      data-testid={`reiki-rebook-attendee-${i}-name`}
                    />
                    <input
                      type="email"
                      placeholder="Email (optional)"
                      value={a.email}
                      onChange={(e) => setAttendees((prev) => prev.map((x, j) => j === i ? { ...x, email: e.target.value } : x))}
                      className="border border-aviana-ink/15 rounded-md px-3 py-2 text-sm"
                    />
                  </div>
                ))}
                {attendees.length < (tier.max_attendees - 1) && (
                  <button
                    type="button"
                    onClick={() => setAttendees((prev) => [...prev, { name: "", email: "" }])}
                    className="text-xs text-aviana-plum hover:text-aviana-goldDeep"
                  >
                    <Plus size={12} className="inline -mt-0.5 mr-1" />
                    Add another
                  </button>
                )}
              </div>
            </div>
          )}

          <div>
            <label className="block text-xs uppercase tracking-wider text-aviana-muted mb-1.5">
              <CalendarDays size={12} className="inline -mt-0.5 mr-1" />Date
            </label>
            <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto border border-aviana-ink/10 rounded-md p-2" data-testid="reiki-rebook-dates">
              {dateOptions.map((d) => {
                const isSelected = selectedDate === d.value;
                const isUnavail = availableDays !== null && !availableDays.has(d.value);
                return (
                  <button
                    key={d.value}
                    onClick={() => { if (!isUnavail) setSelectedDate(d.value); }}
                    disabled={isUnavail && !isSelected}
                    className={`px-2.5 py-1 rounded-full text-xs border transition-all
                      ${isSelected
                        ? "bg-aviana-plum text-white border-aviana-plum"
                        : isUnavail
                        ? "border-aviana-ink/10 text-aviana-ink/35 bg-aviana-ink/[0.03] line-through cursor-not-allowed"
                        : "border-aviana-ink/15 text-aviana-ink hover:border-aviana-gold"}`}
                  >
                    {d.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-wider text-aviana-muted mb-1.5">Time</label>
            {loadingSlots ? (
              <p className="text-sm text-aviana-muted"><Loader2 className="inline animate-spin mr-2" size={14} />Loading availability…</p>
            ) : slots.length === 0 ? (
              <p className="text-sm text-aviana-muted italic">No availability on this date.</p>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-1.5" data-testid="reiki-rebook-slots">
                {slots.map((s) => (
                  <button
                    key={s}
                    onClick={() => setSlot(s)}
                    className={`px-2 py-1.5 rounded-md text-sm border transition-colors
                      ${slot === s ? "bg-aviana-plum text-white border-aviana-plum" : "border-aviana-ink/15 text-aviana-ink hover:border-aviana-gold"}`}
                  >
                    {fmtSlot(s)}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs uppercase tracking-wider text-aviana-muted mb-1.5">Intention (optional)</label>
              <textarea
                rows={2}
                value={intention}
                onChange={(e) => setIntention(e.target.value)}
                className="w-full border border-aviana-ink/15 rounded-md px-3 py-2 text-sm resize-none"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-aviana-muted mb-1.5">Physical notes (optional)</label>
              <textarea
                rows={2}
                value={physicalLimits}
                onChange={(e) => setPhysicalLimits(e.target.value)}
                className="w-full border border-aviana-ink/15 rounded-md px-3 py-2 text-sm resize-none"
              />
            </div>
          </div>

          <div className="rounded-md bg-aviana-sage/10 border border-aviana-sage/30 text-aviana-sage p-3 text-xs">
            <CheckCircle2 size={13} className="inline -mt-0.5 mr-1" />
            Card on file will be used to hold this booking. <strong>No charge now.</strong> Standard cancellation policy applies.
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-aviana-ink/10">
          <button onClick={onClose} className="px-4 py-2 text-sm text-aviana-muted hover:text-aviana-ink">Cancel</button>
          <button
            onClick={submit}
            disabled={submitting || !slot}
            className="btn-primary px-5 py-2 text-sm"
            data-testid="reiki-rebook-submit"
          >
            {submitting ? <><Loader2 className="inline animate-spin mr-1" size={14} />Rebooking…</> : <><Mail size={13} className="inline -mt-0.5 mr-1" />Confirm & send email</>}
          </button>
        </div>
      </div>
    </div>
  );
}
